import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, apiError, API } from "../lib/api";
import { toast } from "sonner";
import { ChevronLeft, Download, ChevronRight } from "lucide-react";

function ym(d) { return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; }

export default function ReconciliationPage() {
    const { siteId } = useParams();
    const [site, setSite] = useState(null);
    const [month, setMonth] = useState(ym(new Date()));
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [s, r] = await Promise.all([api.get(`/sites/${siteId}`), api.get(`/sites/${siteId}/reconciliation`, { params: { month } })]);
            setSite(s.data); setData(r.data);
        } catch (e) { toast.error(apiError(e)); }
        finally { setLoading(false); }
    }, [siteId, month]);
    useEffect(() => { load(); }, [load]);

    async function exportCsv() {
        const token = localStorage.getItem("fx_token");
        const url = `${API}/sites/${siteId}/reconciliation/export.csv?month=${month}`;
        const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
        const blob = await res.blob();
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob); a.download = `reconciliation-${site?.name}-${month}.csv`; a.click();
        URL.revokeObjectURL(a.href);
    }

    async function exportPdf() {
        const token = localStorage.getItem("fx_token");
        const url = `${API}/sites/${siteId}/reconciliation/export.pdf?month=${month}`;
        const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
        const blob = await res.blob();
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob); a.download = `monthly-utilisation-${site?.name?.toLowerCase()}-${month}.pdf`; a.click();
        URL.revokeObjectURL(a.href);
    }

    function shift(delta) {
        const [y, m] = month.split("-").map(Number);
        setMonth(ym(new Date(y, m - 1 + delta, 1)));
    }

    const label = useMemo(() => {
        const [y, m] = month.split("-");
        return new Date(Number(y), Number(m) - 1, 1).toLocaleString("en-IN", { month: "long", year: "numeric" });
    }, [month]);

    return (
        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-6 sm:py-10">
            <Link to={`/sites/${siteId}/consumables`} className="text-xs text-gray-500 hover:text-[#1E3355] inline-flex items-center gap-1 mb-3"><ChevronLeft className="w-3 h-3" /> Back to consumables</Link>
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                <div>
                    <div className="text-xs font-medium uppercase tracking-wider text-gray-500">Monthly Reconciliation</div>
                    <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#1E3355] mt-1">{site?.name || "…"}</h1>
                </div>
                <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-md p-1">
                        <button onClick={() => shift(-1)} className="p-1.5 rounded hover:bg-gray-100" data-testid="recon-prev"><ChevronLeft className="w-4 h-4" /></button>
                        <div className="px-3 text-sm font-medium text-[#1E3355] min-w-[140px] text-center">{label}</div>
                        <button onClick={() => shift(1)} className="p-1.5 rounded hover:bg-gray-100" data-testid="recon-next"><ChevronRight className="w-4 h-4" /></button>
                    </div>
                    <button onClick={exportCsv} className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-md bg-white border border-[#1E3355] text-[#1E3355] hover:bg-[#F4F6F9]" data-testid="recon-export-csv"><Download className="w-3.5 h-3.5" /> Export CSV</button>
                    <button onClick={exportPdf} className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-md bg-[#1E3355] text-white hover:bg-[#15243C]" data-testid="recon-export-pdf"><Download className="w-3.5 h-3.5" /> Monthly Utilisation Statement (PDF)</button>
                </div>
            </div>
            <div className="mt-6 rounded-xl bg-white border border-gray-100 fx-shadow-card overflow-hidden">
                <table className="w-full text-sm" data-testid="reconciliation-table">
                    <thead className="bg-[#F4F6F9]">
                        <tr className="text-left text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                            <th className="px-4 py-3">Item</th>
                            <th className="px-4 py-3 text-right">Opening</th>
                            <th className="px-4 py-3 text-right">Received</th>
                            <th className="px-4 py-3 text-right">Closing</th>
                            <th className="px-4 py-3 text-right">Consumption</th>
                            <th className="px-4 py-3">Billing</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {loading ? <tr><td colSpan="6" className="p-6 text-center text-sm text-gray-500">Loading…</td></tr> :
                        data?.rows.length === 0 ? <tr><td colSpan="6" className="p-6 text-center text-sm text-gray-500" data-testid="recon-empty">No enabled consumables.</td></tr> :
                        data?.rows.map((r) => (
                            <tr key={r.item.id} data-testid={`recon-row-${r.item.name}`}>
                                <td className="px-4 py-3 font-medium text-[#1E3355]">{r.item.name} <span className="text-[10px] text-gray-400">/{r.item.unit}</span></td>
                                <td className="px-4 py-3 text-right tabular-nums">{r.opening}</td>
                                <td className="px-4 py-3 text-right tabular-nums text-emerald-700">{r.received}</td>
                                <td className="px-4 py-3 text-right tabular-nums">{r.closing}</td>
                                <td className="px-4 py-3 text-right tabular-nums font-semibold text-[#1E3355]">{r.implied_consumption}</td>
                                <td className="px-4 py-3 text-xs">{r.billing_treatment === "BILLED_TO_CLIENT" ? <span className="text-amber-700 font-medium">Billed</span> : <span className="text-gray-500">Included</span>}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
